import type { HeliosTranslations } from 'types/dataHelios';

type HeliosLanguageContext = { language?: string; resolvedLanguage?: string } | undefined;

const isObjectRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const normalizeLanguage = (language?: string): string | undefined => {
  if (!language) {
    return undefined;
  }

  const normalized = language.trim().toLowerCase().replace('_', '-');
  const primary = normalized.split('-')[0];

  const aliases: Record<string, 'cs' | 'en' | 'sk'> = {
    cs: 'cs',
    cz: 'cs',
    czech: 'cs',
    en: 'en',
    eng: 'en',
    english: 'en',
    sk: 'sk',
    slovak: 'sk',
  };

  return aliases[primary] ?? primary;
};

export const getCurrentLanguageContent = (i18n: HeliosLanguageContext, arr?: HeliosTranslations[]): string | undefined => {
  const currentLanguage = normalizeLanguage(i18n?.resolvedLanguage ?? i18n?.language);

  if (!arr?.length) {
    return undefined;
  }

  const content = arr.find((item) => normalizeLanguage(item.language) === currentLanguage)?.value;

  if (content && content.trim().length > 0) {
    return content;
  }

  const fallbackOrder = ['cs', 'sk', 'en'];
  const fallbackContent = fallbackOrder
    .map((language) => arr.find((item) => normalizeLanguage(item.language) === language)?.value)
    .find((value) => Boolean(value && value.trim().length > 0));

  return fallbackContent ?? undefined;
};

type FormLayoutLabelsSource = {
  sections?: Array<{
    items: Array<{
      itemDatasource?: { templateDataItemId?: number | null };
      label?: HeliosTranslations[] | null;
    }>;
  }>;
};

export const getFormLayoutTranslations = (i18n: HeliosLanguageContext, formLayout?: FormLayoutLabelsSource): Record<number, string> => {
  const translations: Record<number, string> = {};

  formLayout?.sections?.forEach((section) => {
    section.items.forEach((item) => {
      const itemId = item.itemDatasource?.templateDataItemId;

      if (itemId == null || translations[itemId] !== undefined) {
        return;
      }

      const label = getCurrentLanguageContent(i18n, item.label ?? undefined);
      if (label) {
        translations[itemId] = label;
      }
    });
  });

  return translations;
};

type HeliosFunctionAliasSource = {
  alias?: string;
  mobileAlias?: string;
  aliasTranslations?: HeliosTranslations[];
  mobileAliasTranslations?: HeliosTranslations[];
};

export const getLocalizedHeliosFunctionAlias = (i18n: HeliosLanguageContext, func?: HeliosFunctionAliasSource, isMobile = false): string => {
  if (!func) {
    return '';
  }

  const primaryTranslations = isMobile ? func.mobileAliasTranslations : func.aliasTranslations;
  const fallbackTranslations = isMobile ? func.aliasTranslations : func.mobileAliasTranslations;

  return (
    getCurrentLanguageContent(i18n, primaryTranslations) ||
    getCurrentLanguageContent(i18n, fallbackTranslations) ||
    (isMobile ? func.mobileAlias || func.alias : func.alias) ||
    ''
  );
};

const getStringField = (record: Record<string, unknown>, key: string): string | undefined => {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
};

const toHeliosTranslation = (item: unknown): HeliosTranslations | undefined => {
  if (!isObjectRecord(item)) {
    return undefined;
  }

  const language = getStringField(item, 'language') ?? getStringField(item, 'key') ?? getStringField(item, 'culture');
  const value =
    getStringField(item, 'value') ?? getStringField(item, 'text') ?? getStringField(item, 'label') ?? getStringField(item, 'name') ?? undefined;

  if (!language || !value) {
    return undefined;
  }

  return { language, value };
};

const getLanguageMapContent = (i18n: HeliosLanguageContext, source: Record<string, unknown>): string | undefined => {
  const currentLanguage = normalizeLanguage(i18n?.resolvedLanguage ?? i18n?.language);
  const order = [currentLanguage, 'cs', 'sk', 'en'].filter(
    (language, index, arr): language is string => Boolean(language) && arr.indexOf(language) === index
  );

  for (const language of order) {
    const aliases =
      language === 'cs' ? ['cs', 'cz', 'czech'] : language === 'en' ? ['en', 'eng', 'english'] : language === 'sk' ? ['sk', 'slovak'] : [language];

    for (const alias of aliases) {
      const value = source[alias];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }
  }

  return undefined;
};

const getTranslationsFromUnknown = (i18n: HeliosLanguageContext, source: unknown): HeliosTranslations[] | undefined => {
  if (!isObjectRecord(source)) {
    return undefined;
  }

  const translationKeys = [
    'translations',
    'translates',
    'translatesLabel',
    'translatesColumnName',
    'translateLabels',
    'translatesName',
    'labelTranslations',
    'labelTranslate',
  ];

  for (const key of translationKeys) {
    const candidate = source[key];
    if (Array.isArray(candidate)) {
      const parsed = candidate.reduce<HeliosTranslations[]>((acc, item) => {
        const translation = toHeliosTranslation(item);
        if (translation) {
          acc.push(translation);
        }
        return acc;
      }, []);

      if (parsed.length > 0) {
        return parsed;
      }
    }

    if (isObjectRecord(candidate)) {
      const mapped = getLanguageMapContent(i18n, candidate);
      if (mapped) {
        return [{ language: normalizeLanguage(i18n?.resolvedLanguage ?? i18n?.language) ?? 'cs', value: mapped }];
      }
    }
  }

  return undefined;
};

export const getLocalizedHeliosColumnName = (i18n: HeliosLanguageContext, column?: unknown): string => {
  if (!column || !isObjectRecord(column)) {
    return '';
  }

  const translated = getCurrentLanguageContent(i18n, getTranslationsFromUnknown(i18n, column));
  if (translated && translated.trim().length > 0) {
    return translated;
  }

  const label = getStringField(column, 'label');
  if (label && label.trim().length > 0) {
    return label;
  }

  return '';
};
